<template>
	<div class="individuals-panel">
		<header class="individuals-panel__head">
			<h1 class="individuals-panel__heading">{{ translate( "heading" ) }}</h1>
		</header>
		<main class="individuals-panel__body">
			<section class="individuals-panel__section">
				<p>{{ translate( "description" ) }}</p>
			</section>
			<section class="individuals-panel__section individuals-panel__members-section">
				<MemberAssessmentRow
					:className="rowClasses( assessment )"
					v-for="assessment in assessments"
					:key="assessment.uiKey"
					:assessment="assessment"
					@click="onAssessmentRowClicked( assessment )"
					v-on="bubble( `takeWorkStyleAssessment` )"
				/>
			</section>
		</main>
		<footer class="individuals-panel__footer">
			<p v-html="translate( `discDescription` )"></p>
		</footer>
		<TombstonePopover
			:class="tombstoneClasses"
			:popoverVisibility="popoverVisibility"
			@close="onTombstoneClose"
		>
			<IndividualTombstonePanel
				v-if="selectedUser"
				:user="selectedUser"
				@close="onTombstoneClose"
			/>
		</TombstonePopover>
		<IndividualDialog
			v-if="selectedUser"
			:className="dialogClasses"
			:user="selectedUser"
			:showDialog="isPopoverVisible"
			@hide="onDialogHide"
		/>
	</div>
</template>

<script>
import { PopoverVisibility } from "@teaming/common-enums";

import {
	TranslateMixin,
	EventsMixin,
	TombstonePopover
} from "@teaming/common-ui";

import MemberAssessmentRow from "./MemberAssessmentRow";
import IndividualTombstonePanel from "./IndividualTombstonePanel";
import IndividualDialog from "./IndividualDialog";

export default {
	name: "IndividualsPanel",
	teamingPackage: "common-team",
	mixins: [ TranslateMixin, EventsMixin, ],
	emits: [ "takeWorkStyleAssessment", ],
	components: {
		MemberAssessmentRow,
		TombstonePopover,
		IndividualTombstonePanel,
		IndividualDialog,
	},
	props: {
		assessments: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			selectedUser: null,
			popoverVisibility: PopoverVisibility.INDETERMINATE,
			windowResizeHandle: null,
		};
	},
	computed: {
		isPopoverVisible() {
			return this.popoverVisibility === PopoverVisibility.VISIBLE;
		},
		tombstoneClasses() {
			const baseClass = "individuals-panel__tombstone-popover";

			if ( this.isPopoverVisible ) {
				return `${ baseClass } individuals-panel__tombstone-popover--visible`;
			}

			return baseClass;
		},
		dialogClasses() {
			const baseClass = "individuals-panel__dialog";

			if ( this.isPopoverVisible ) {
				return `${ baseClass } individuals-panel__dialog--visible`;
			}

			return baseClass;
		},
	},
	methods: {
		rowClasses( assessment ) {
			const classes = "individuals-panel__assessment-row";

			if ( assessment.isComplete ) {
				return `${ classes } individuals-panel__assessment-row--clickable`;
			}

			return classes;
		},
		onAssessmentRowClicked( assessment ) {
			if ( !assessment.isComplete ) {
				return;
			}
			this.selectedUser = assessment.member;
			this.popoverVisibility = PopoverVisibility.VISIBLE;
		},
		onTombstoneClose() {
			this.popoverVisibility = PopoverVisibility.HIDDEN;
		},
		onDialogHide() {
			this.popoverVisibility = PopoverVisibility.HIDDEN;
		},
	},
	mounted() {
		if ( !this.windowResizeHandle ) {
			this.windowResizeHandle = () => {
				this.popoverVisibility = PopoverVisibility.HIDDEN;
			};
			window.addEventListener( "resize", this.windowResizeHandle );
		}
	},
	beforeUnmount() {
		if ( this.windowResizeHandle ) {
			window.removeEventListener( "resize", this.windowResizeHandle );
			this.windowResizeHandle = null;
		}
	},
};
</script>

<style lang="scss" scoped>
.individuals-panel {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	p, li {
		color: $individuals-panel-text-color;
		@include font-and-line-for(0);
	}

	ul {
		list-style: disc;
	}
}

.individuals-panel__heading {
	color: $individuals-panel-heading-color;
	@include font-and-line-for(1);
	font-weight: 600;
}

.individuals-panel__body {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.individuals-panel__footer {
	padding-top: 1rem;

	p {
		@include font-and-line-for(-2);
	}
}

.individuals-panel__members-section {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.individuals-panel__assessment-row--clickable {
	cursor: pointer;
}

// NOTE: we are using the same state data to display both
// the tombstone and the dialog, and then FORCIBLY hiding
// both with CSS at the appropriate breakpoints, regardless
// of the state variable.
//
.individuals-panel__tombstone-popover {
	display: none;
}

.individuals-panel__tombstone-popover--visible {
	display: block;

	@include for-size-and-larger('sm') {
		display: none;
	}
}
</style>

<style lang="scss">
// NOTE: dialog styles must be unscoped to take affect
// on the dialog popup. The p-dialog class must be used
// for specificity.
.individuals-panel__dialog.p-dialog {
	display: none;
}

.individuals-panel__dialog--visible.p-dialog {
	display: flex;

	@include for-size-and-smaller('xs') {
		display: none;
	}
}

.p-dialog-mask {
	z-index: 1001 !important; // default value

	@include for-size-and-smaller('xs') {
		z-index: 0 !important; // kill this overlay for the tombstone
	}
}
</style>
